<template>
  <v-expansion-panel>
    <v-expansion-panel-header class="section-panel-header">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="36" class="mr-3 panel-avatar">
          <v-icon dark small>tune</v-icon>
        </v-avatar>
        <div>
          <div class="section-title">Paramètres du magasin</div>
          <div class="section-subtitle">{{ metier.key === 'boucherie' ? 'Ville, étiquettes, équipe & fournisseurs' : 'TVA, ville, étiquettes, équipe & fournisseurs' }}</div>
        </div>
      </div>
      <template v-slot:actions>
        <div class="d-flex align-center mr-2" style="gap: 4px;">
          <v-icon x-small :color="city.name ? 'success' : 'grey lighten-1'">{{ city.name ? 'check_circle' : 'radio_button_unchecked' }}</v-icon>
          <v-icon x-small :color="notilus.etiquette != null && notilus.etiquette !== '' ? 'success' : 'grey lighten-1'">{{ notilus.etiquette != null && notilus.etiquette !== '' ? 'check_circle' : 'radio_button_unchecked' }}</v-icon>
          <v-icon x-small :color="employees.length > 0 ? 'success' : 'grey lighten-1'">{{ employees.length > 0 ? 'check_circle' : 'radio_button_unchecked' }}</v-icon>
        </div>
        <v-icon color="primary">$expand</v-icon>
      </template>
    </v-expansion-panel-header>
    <v-expansion-panel-content>

      <!-- ── BLOC TVA / VILLE / CESSION ─────────────────── -->
      <v-row class="mt-3 px-2">
        <!-- TVA -->
        <v-col v-if="metier.sections.tva" cols="12" md="3">
          <v-card outlined class="config-card pa-3 fill-height">
            <div class="config-card-title">
              <v-icon small color="primary" class="mr-1">percent</v-icon>Taux de TVA
            </div>
            <v-radio-group :value="tva" @change="$emit('update:tva', $event); $emit('save')" row hide-details class="mt-2 mb-0">
              <v-radio color="primary" label="5,5 %" :value="5.5" />
              <v-radio color="primary" label="2,1 %" :value="2.1" />
            </v-radio-group>
          </v-card>
        </v-col>

        <!-- VILLE -->
        <v-col cols="12" :md="metier.key === 'boucherie' ? 6 : 5">
          <v-card outlined class="config-card pa-3 fill-height" :class="{ 'config-card--done': !!city.name }">
            <div class="config-card-title">
              <v-icon small color="primary" class="mr-1">apartment</v-icon>Ville du magasin
              <v-icon v-if="city.name" x-small color="success" class="ml-1">check_circle</v-icon>
            </div>
            <template v-if="!city.name || updateCity">
              <v-autocomplete
                :value="city"
                @input="$emit('update:city', $event); if ($event) { $emit('update:updateCity', false); $emit('save') }"
                :items="cities"
                :loading="isLoading"
                :search-input="citysearch"
                @update:search-input="$emit('update:citysearch', $event)"
                hide-no-data hide-selected
                color="primary"
                item-text="name"
                append-icon="done"
                @click:append="$emit('update:updateCity', false)"
                clearable
                placeholder="Recherchez votre ville"
                return-object
                dense outlined hide-details
                class="mt-2"
              />
            </template>
            <template v-else>
              <div class="d-flex align-center mt-3">
                <v-chip color="secondary" dark label class="mr-2">
                  <v-icon left small>apartment</v-icon>{{ city.name }}
                </v-chip>
                <v-btn icon x-small @click="$emit('update:updateCity', true)">
                  <v-icon small color="grey">edit</v-icon>
                </v-btn>
              </div>
            </template>
          </v-card>
        </v-col>

        <!-- CESSION -->
        <v-col v-if="metier.sections.retrocession" cols="12" md="4">
          <v-card outlined class="config-card pa-3 fill-height" :class="{ 'config-card--done': cession !== '' && cession != null }">
            <div class="config-card-title">
              <v-icon small color="primary" class="mr-1">swap_horiz</v-icon>Rétro-cession
              <v-icon v-if="cession !== '' && cession != null" x-small color="success" class="ml-1">check_circle</v-icon>
            </div>
            <p class="config-card-hint">Votre rayon fait-il des cessions à d'autres rayons ?</p>
            <v-radio-group :value="cession" @change="$emit('update:cession', $event)" row hide-details class="mt-0 mb-0">
              <v-radio color="primary" label="Oui" value="true" />
              <v-radio color="primary" label="Non" value="false" />
            </v-radio-group>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── GLN ──────────────────────────────────────────── -->
      <v-row v-if="glnEnabled" class="px-2">
        <v-col cols="12" md="6">
          <v-card outlined class="config-card pa-3 fill-height" :class="{ 'config-card--done': glnIsValid }">
            <div class="config-card-title">
              <v-icon small color="primary" class="mr-1">assignment</v-icon>{{ glnMode.key === 'leclerc' ? 'Numéro GLN' : 'Code de routage ou SIREN' }}
              <v-icon v-if="glnIsValid" x-small color="success" class="ml-1">check_circle</v-icon>
            </div>
            <p v-if="glnMode.key === 'leclerc'" class="config-card-hint">Votre GLN de facturation, nécessaire pour la facturation électronique, se construit SIRET_GLN : le SIRET de votre société (14 chiffres ; le SIREN à 9 chiffres est aussi accepté), un tiret bas, puis le GLN de votre magasin (13 chiffres). Par exemple {{ glnMode.example }}. Votre centrale ou votre service comptabilité peut vous le communiquer.</p>
            <p v-else class="config-card-hint">Selon votre enseigne (Intermarché, Super U…), renseignez le code de routage de facturation électronique communiqué par votre centrale, ou à défaut le SIREN de votre société (9 chiffres). Votre centrale ou votre service comptabilité peut vous le communiquer.</p>
            <v-text-field
              :value="gln"
              @input="$emit('update:gln', $event)"
              @blur="$emit('save-gln')"
              @keyup.enter="$emit('save-gln')"
              :label="glnMode.label"
              :hint="glnMode.hint"
              :placeholder="glnMode.example"
              persistent-hint
              inputmode="text"
              autocomplete="off"
              maxlength="30"
              :counter="glnMode.counter"
              :counter-value="countGlnDigits"
              :rules="glnRules"
              dense outlined
              class="mt-2"
            />
          </v-card>
        </v-col>
      </v-row>

      <!-- ── ÉTIQUETTES ───────────────────────────────────── -->
      <template v-if="metier.sections.etiquettes">
        <div class="sub-section-title mt-5 mb-3 mx-2">
          <v-icon color="primary" small class="mr-2">sell</v-icon>Mode d'impression des étiquettes
          <v-chip v-if="notilus.etiquette != null && notilus.etiquette !== ''" x-small color="success" dark class="ml-2">Sélectionné</v-chip>
        </div>
        <v-row justify="center" class="px-2" style="align-items: stretch;">
          <v-col cols="12" sm="4" class="d-flex">
            <v-hover v-slot="{ hover }" style="width: 100%;">
              <v-card
                class="etiquette-card"
                :class="{ 'selected-card': notilus.etiquette === 1, 'elevation-4': hover, 'elevation-1': !hover, 'mouse': true }"
                @click="$emit('select-etiquette', 1)"
              >
                <v-card-title class="etiquette-card-title" :class="{ 'selected-title': notilus.etiquette === 1 }">
                  Hublot
                  <v-spacer />
                  <v-icon v-if="notilus.etiquette === 1" color="secondary" small>check_circle</v-icon>
                </v-card-title>
                <v-card-text class="etiquette-container text-center">
                  <img src="@/assets/etiquettes/hublot.png" height="200" class="etiquette-img" />
                  <div class="card-desc mt-3">
                    <v-icon large color="primary">self_improvement</v-icon>
                    <span class="ml-3 text-left">Imprimante laser fournie<br><small>Pics prix (×100), plaques (×100), ramette (×200)</small></span>
                  </div>
                </v-card-text>
              </v-card>
            </v-hover>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex">
            <v-hover v-slot="{ hover }" style="width: 100%;">
              <v-card
                class="etiquette-card"
                :class="{ 'selected-card': isBlackPrinterSelected, 'elevation-4': hover, 'elevation-1': !hover }"
              >
                <v-card-title class="etiquette-card-title" :class="{ 'selected-title': isBlackPrinterSelected }">
                  Étiquette noire
                  <v-spacer />
                  <v-icon v-if="isBlackPrinterSelected" color="secondary" small>check_circle</v-icon>
                </v-card-title>
                <v-card-text class="etiquette-container text-center">
                  <img src="@/assets/etiquettes/evolis.png" height="200" class="etiquette-img" />
                  <div class="mt-3">
                    <v-select
                      :value="currentBlackPrinter"
                      @change="$emit('select-etiquette', $event)"
                      item-text="nom"
                      label="Choisissez votre modèle"
                      item-value="idPrinter"
                      :items="blackPrinters"
                      dense outlined hide-details
                      return-object
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-hover>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex">
            <v-hover v-slot="{ hover }" style="width: 100%;">
              <v-card
                class="etiquette-card"
                :class="{ 'selected-card': notilus.etiquette === 2, 'elevation-4': hover, 'elevation-1': !hover, 'mouse': true }"
                @click="$emit('select-etiquette', 2)"
              >
                <v-card-title class="etiquette-card-title" :class="{ 'selected-title': notilus.etiquette === 2 }">
                  Étiquette électronique
                  <v-spacer />
                  <v-icon v-if="notilus.etiquette === 2" color="secondary" small>check_circle</v-icon>
                </v-card-title>
                <v-card-text class="etiquette-container text-center">
                  <div class="esl-visual my-2">
                    <svg viewBox="0 0 200 130" width="180" xmlns="http://www.w3.org/2000/svg">
                      <rect x="4" y="4" width="192" height="122" rx="10" ry="10" fill="#e8ecef" stroke="#b0bec5" stroke-width="2"/>
                      <rect x="14" y="12" width="172" height="90" rx="4" ry="4" fill="#f5f7f8" stroke="#cfd8dc" stroke-width="1.5"/>
                      <text x="100" y="34" text-anchor="middle" font-size="10" font-weight="bold" fill="#263238" font-family="sans-serif">{{ metier.key === 'boucherie' ? 'FILET DE BOEUF' : 'CREVETTES GRISES' }}</text>
                      <line x1="20" y1="40" x2="180" y2="40" stroke="#cfd8dc" stroke-width="1"/>
                      <text x="100" y="68" text-anchor="middle" font-size="26" font-weight="bold" fill="#0d47a1" font-family="sans-serif">12,90€</text>
                      <text x="100" y="84" text-anchor="middle" font-size="8" fill="#607d8b" font-family="sans-serif">au kg — origine France</text>
                      <g transform="translate(60,90)">
                        <rect x="0" y="0" width="3" height="10" fill="#263238"/><rect x="5" y="0" width="1" height="10" fill="#263238"/><rect x="8" y="0" width="2" height="10" fill="#263238"/><rect x="12" y="0" width="3" height="10" fill="#263238"/><rect x="17" y="0" width="1" height="10" fill="#263238"/><rect x="20" y="0" width="2" height="10" fill="#263238"/><rect x="24" y="0" width="3" height="10" fill="#263238"/><rect x="29" y="0" width="1" height="10" fill="#263238"/><rect x="32" y="0" width="2" height="10" fill="#263238"/><rect x="36" y="0" width="3" height="10" fill="#263238"/><rect x="41" y="0" width="1" height="10" fill="#263238"/><rect x="44" y="0" width="2" height="10" fill="#263238"/><rect x="48" y="0" width="3" height="10" fill="#263238"/><rect x="53" y="0" width="1" height="10" fill="#263238"/><rect x="56" y="0" width="2" height="10" fill="#263238"/><rect x="60" y="0" width="3" height="10" fill="#263238"/><rect x="65" y="0" width="1" height="10" fill="#263238"/><rect x="68" y="0" width="2" height="10" fill="#263238"/>
                      </g>
                      <circle cx="186" cy="118" r="4" fill="#4caf50"/>
                      <text x="100" y="110" text-anchor="middle" font-size="7" fill="#90a4ae" font-family="sans-serif" letter-spacing="2">VUSION</text>
                    </svg>
                  </div>
                  <div class="card-desc mt-2">
                    <v-icon large color="primary">electrical_services</v-icon>
                    <span class="ml-3 text-left">Système Vusion — e-ink sans fil<br><small>Mise à jour à distance, batterie longue durée</small></span>
                  </div>
                  <v-alert dense text color="info" class="mt-3 text-left" icon="info">
                    <span class="caption"><strong>Étude préalable requise</strong><br>La mise en place d'étiquettes électroniques nécessite une étude technique personnalisée. Notre équipe vous contactera pour l'évaluer.</span>
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
      </template>

      <!-- ── EMPLOYÉS ────────────────────────────────────── -->
      <div class="sub-section-title mt-5 mb-3 mx-2">
        <v-icon color="primary" small class="mr-2">group</v-icon>Employés
        <v-chip x-small :color="employees.length > 0 ? 'success' : 'grey lighten-1'" :dark="employees.length > 0" class="ml-2">{{ employees.length }}</v-chip>
      </div>
      <v-row class="px-2">
        <v-col cols="12" md="4">
          <v-card outlined class="config-card pa-3">
            <div class="config-card-title mb-3">
              <v-icon small color="primary" class="mr-1">person_add</v-icon>Ajouter un employé
            </div>
            <v-row dense>
              <v-col cols="6">
                <v-text-field v-model="newEmployee.nom" label="Nom" dense outlined hide-details="auto" class="mb-2" />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="newEmployee.prenom"
                  label="Prénom"
                  dense outlined hide-details="auto"
                  class="mb-2"
                  :error="duplicateEmployee"
                />
              </v-col>
            </v-row>
            <v-alert v-if="duplicateEmployee" dense text type="warning" class="mb-2 caption">Cet employé existe déjà</v-alert>
            <v-btn color="secondary" dark block :disabled="invalidEmployee" @click="onAddEmployee">
              <v-icon left small>add</v-icon>Ajouter
            </v-btn>
          </v-card>
        </v-col>
        <v-col cols="12" md="8">
          <div v-if="employees.length === 0" class="empty-list-hint">
            <v-icon color="grey lighten-1" class="mr-2">person_off</v-icon>Aucun employé ajouté
          </div>
          <div v-else class="chips-wrap">
            <v-chip
              v-for="(employee, employeeIndex) in employees"
              :key="employee.idemployees || employee.idEmployees || employeeIndex"
              close
              color="primary"
              outlined
              class="mr-2 mb-2"
              @click:close="$emit('remove-employee', employee)"
            >
              <v-icon left small>person</v-icon>
              {{ employee.prenom }} {{ employee.nom }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <!-- ── FOURNISSEURS ────────────────────────────────── -->
      <div class="sub-section-title mt-5 mb-3 mx-2">
        <v-icon color="primary" small class="mr-2">local_shipping</v-icon>Fournisseurs
        <v-chip x-small :color="fournisseursDisplay.length > 0 ? 'success' : 'grey lighten-1'" :dark="fournisseursDisplay.length > 0" class="ml-2">{{ fournisseursDisplay.length }}</v-chip>
      </div>
      <v-row class="px-2">
        <v-col cols="12" md="4">
          <v-card outlined class="config-card pa-3">
            <div class="config-card-title mb-3">
              <v-icon small color="primary" class="mr-1">add_business</v-icon>Ajouter un fournisseur
            </div>
            <v-text-field v-model="newFournisseur.nom" label="Nom du fournisseur" dense outlined hide-details class="mb-2" />
            <v-switch v-model="newFournisseur.iscentrale" color="primary" label="Centrale d'achat" dense hide-details class="mb-3 mt-1" />
            <v-btn color="secondary" dark block :disabled="invalidFournisseur" @click="onAddFournisseur">
              <v-icon left small>add</v-icon>Ajouter
            </v-btn>
          </v-card>
        </v-col>
        <v-col cols="12" md="8">
          <div v-if="fournisseursDisplay.length === 0" class="empty-list-hint">
            <v-icon color="grey lighten-1" class="mr-2">local_shipping</v-icon>Aucun fournisseur ajouté
          </div>
          <div v-else class="chips-wrap">
            <v-chip
              v-for="(fournisseur, fournisseurIndex) in fournisseursDisplay"
              :key="fournisseur.idfournisseur || fournisseurIndex"
              close
              :color="fournisseur.icon ? 'secondary' : 'primary'"
              outlined
              class="mr-2 mb-2"
              @click:close="$emit('remove-fournisseur', fournisseur)"
            >
              <v-icon left small>{{ fournisseur.icon ? 'store' : 'local_shipping' }}</v-icon>
              {{ fournisseur.nom }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { cleanGln, glnModeFor } from '@/utils/glnUtils'

export default {
  name: 'ConfigurationSection',
  props: {
    metier: { type: Object, required: true },
    tva: { type: [Number, String], default: 5.5 },
    cession: { type: String, default: '' },
    city: { type: Object, default: () => ({}) },
    cities: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    citysearch: { type: String, default: '' },
    updateCity: { type: Boolean, default: false },
    notilus: { type: Object, default: () => ({}) },
    currentBlackPrinter: { type: [Object, String], default: '' },
    blackPrinters: { type: Array, default: () => [] },
    employees: { type: Array, default: () => [] },
    fournisseurs: { type: Array, default: () => [] },
    gln: { type: String, default: '' },
    glnEnabled: { type: Boolean, default: false },
    glnEnseigne: { type: String, default: '' },
  },
  data () {
    return {
      newEmployee: { nom: '', prenom: '' },
      newFournisseur: { nom: '', iscentrale: false },
    }
  },
  computed: {
    isBlackPrinterSelected () {
      const value = this.notilus.etiquette
      return value !== 1 && value !== 2 && value !== '' && value != null
    },
    glnMode () {
      return glnModeFor(this.glnEnseigne)
    },
    glnRules () {
      return [
        (value) => !cleanGln(value) || this.glnMode.isValid(value) || `Format attendu : ${this.glnMode.hint} (ex. ${this.glnMode.example})`,
      ]
    },
    glnIsValid () {
      return this.glnMode.isValid(this.gln)
    },
    duplicateEmployee () {
      return this.newEmployee.nom.length > 0 && this.newEmployee.prenom.length > 0 &&
        this.employees.some(emp => emp.nom === this.newEmployee.nom && emp.prenom === this.newEmployee.prenom)
    },
    invalidEmployee () {
      return this.newEmployee.nom.length < 1 || this.newEmployee.prenom.length < 2 || this.duplicateEmployee
    },
    invalidFournisseur () {
      return this.newFournisseur.nom.length < 1 ||
        this.fournisseurs.some(f => f.nom === this.newFournisseur.nom && f.iscentrale === this.newFournisseur.iscentrale)
    },
    fournisseursDisplay () {
      return this.fournisseurs
        .filter(f => !f.isinternal)
        .map(f => ({ ...f, icon: f.iscentrale }))
    },
  },
  methods: {
    countGlnDigits (value) {
      return cleanGln(value).length
    },
    onAddEmployee () {
      if (this.invalidEmployee) return
      this.$emit('add-employee', { nom: this.newEmployee.nom, prenom: this.newEmployee.prenom })
      this.newEmployee.nom = ''
      this.newEmployee.prenom = ''
    },
    onAddFournisseur () {
      if (this.invalidFournisseur) return
      this.$emit('add-fournisseur', { nom: this.newFournisseur.nom, iscentrale: this.newFournisseur.iscentrale })
      this.newFournisseur.nom = ''
      this.newFournisseur.iscentrale = false
    },
  },
}
</script>

<style scoped>
.etiquette-card {
  height: 100%;
  border: 2px solid transparent !important;
  transition: border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
}
.selected-card {
  border: 2.5px solid var(--v-secondary-base) !important;
  box-shadow: 0 0 0 4px rgba(0, 120, 90, 0.12) !important;
}
.config-card {
  border-radius: 10px !important;
  border: 1.5px solid #dde5ea !important;
  transition: border-color 0.2s;
}
.config-card--done {
  border-color: #4caf50 !important;
  background: rgba(76, 175, 80, 0.03) !important;
}
.config-card-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}
.config-card-hint {
  font-size: 0.78rem;
  color: rgba(0,0,0,0.45);
  margin-bottom: 4px;
}
.sub-section-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
}
.chips-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 8px 0;
  min-height: 48px;
}
.empty-list-hint {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: rgba(0,0,0,0.38);
  padding: 16px 4px;
}
</style>
